import React, { useEffect, useMemo, useRef, useState } from "react";

const PALETTE = {
  purpleStart: "#667eea",
  purpleEnd: "#764ba2",
  goldStart: "#ffd700",
  goldEnd: "#ffed4e",
  green: "#4CAF50",
  telegramStart: "#0088cc",
  telegramEnd: "#229ed9",
  orangeStart: "#f7931e",
  orangeEnd: "#ffa500",
  bgDark: "#1e1e1e",
  bgMid: "#2a2a2a",
  bgDeep: "#141414",
};

// Дефолтные планы (fallback). Актуальные цены приходят с сервера
// через GET /api/payment/plans (.env) и передаются пропом `plans`.
const DEFAULT_PLANS = [
  {
    id: "month",
    label: "1 month",
    duration: "30 days of premium access",
    durationDays: 30,
    price: 500,
    monthly: "500 Stars / month",
    tagline: "Quick start",
    blurb: "Full premium access for a short focused sprint.",
    accentStart: PALETTE.telegramStart,
    accentEnd: PALETTE.telegramEnd,
  },
  {
    id: "quarter",
    label: "3 months",
    duration: "90 days of premium access",
    durationDays: 90,
    price: 1000,
    monthly: "333 Stars / month",
    tagline: "Most flexible",
    blurb: "More room to explore the catalog with a lower monthly rate.",
    accentStart: PALETTE.green,
    accentEnd: "#66bb6a",
  },
  {
    id: "half-year",
    label: "6 months",
    duration: "180 days of premium access",
    durationDays: 180,
    price: 1500,
    monthly: "250 Stars / month",
    tagline: "Recommended",
    blurb: "The strongest value balance for regular premium members.",
    accentStart: PALETTE.goldStart,
    accentEnd: PALETTE.goldEnd,
    featured: true,
  },
  {
    id: "year",
    label: "1 year",
    duration: "365 days of premium access",
    durationDays: 365,
    price: 2500,
    monthly: "208 Stars / month",
    tagline: "Best long-term",
    blurb: "Lock in premium for the year and stay in the fast lane.",
    accentStart: PALETTE.purpleStart,
    accentEnd: PALETTE.purpleEnd,
    highlighted: true,
    highlightLabel: "Best value",
  },
];

const BENEFITS = [
  {
    id: "library",
    strong: "Full CGPeers library",
    suffix: " included",
  },
  {
    id: "downloads",
    strong: "Unlimited downloads",
    suffix: " & premium content",
  },
  {
    id: "features",
    prefix: "Early access to ",
    strong: "new features",
  },
  {
    id: "updates",
    strong: "Daily",
    suffix: " content updates",
  },
];

const BEAM_PARTICLE_COUNT = 32;
const BEAM_PARTICLES = Array.from({ length: BEAM_PARTICLE_COUNT }, (_, index) => {
  const angle = (index / BEAM_PARTICLE_COUNT) * Math.PI * 2;
  const ring = 0.22 + (index % 4) * 0.1;
  const top = 50 + Math.sin(angle) * ring * 78 + ((index % 3) - 1) * 3;
  const left = 50 + Math.cos(angle) * ring * 88 + ((index % 4) - 1.5) * 2.5;

  return {
    top: Math.min(90, Math.max(10, top)),
    left: Math.min(92, Math.max(8, left)),
    size: 2 + (index % 5) * 0.9,
    blur: 0.2 + (index % 4) * 0.35,
    delay: -(index * 0.65),
    drift: 10 + (index % 7) * 2.2,
    twinkle: 1.4 + (index % 6) * 0.45,
    bright: index % 3 === 0,
    soft: index % 4 === 1,
  };
});

const styles = `
  .pmx-root,
  .pmx-root * {
    box-sizing: border-box;
  }

  .pmx-root {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    font-family: "Segoe UI", "Trebuchet MS", sans-serif;
    -webkit-tap-highlight-color: transparent;
  }

  .pmx-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(10px);
  }

  .pmx-window {
    position: relative;
    width: min(560px, calc(100vw - 32px));
    overflow: hidden;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background:
      linear-gradient(
        to bottom,
        rgba(30, 30, 30, 0.96) 0%,
        rgba(42, 42, 42, 0.98) 40%,
        rgba(30, 30, 30, 0.99) 70%,
        rgba(20, 20, 20, 1) 100%
      ),
      linear-gradient(145deg, #1e1e1e, #2a2a2a);
    box-shadow:
      0 25px 50px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
    color: #fff;
  }

  .pmx-window::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(135deg, rgba(255, 255, 255, 0.06), transparent 26%),
      radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.06), transparent 22%);
    pointer-events: none;
  }

  .pmx-noise {
    position: absolute;
    inset: -12%;
    pointer-events: none;
    opacity: 0.85;
    filter: blur(1.8px);
    mask-image: radial-gradient(ellipse 85% 75% at 50% 42%, black 20%, transparent 74%);
    background-image:
      radial-gradient(ellipse 58% 48% at 16% 20%, rgba(102, 126, 234, 0.1), transparent 70%),
      radial-gradient(ellipse 52% 42% at 84% 24%, rgba(118, 75, 162, 0.08), transparent 68%),
      radial-gradient(ellipse 62% 52% at 74% 80%, rgba(255, 215, 0, 0.06), transparent 72%),
      radial-gradient(ellipse 48% 58% at 10% 74%, rgba(102, 126, 234, 0.05), transparent 70%),
      radial-gradient(circle at 18% 22%, rgba(255, 255, 255, 0.14) 0.4px, transparent 1.6px),
      radial-gradient(circle at 42% 14%, rgba(200, 230, 255, 0.1) 0.3px, transparent 1.2px),
      radial-gradient(circle at 68% 28%, rgba(255, 255, 255, 0.11) 0.35px, transparent 1.4px),
      radial-gradient(circle at 86% 18%, rgba(180, 220, 255, 0.09) 0.3px, transparent 1.2px),
      radial-gradient(circle at 24% 58%, rgba(255, 255, 255, 0.1) 0.35px, transparent 1.4px),
      radial-gradient(circle at 52% 72%, rgba(200, 230, 255, 0.08) 0.3px, transparent 1.2px),
      radial-gradient(circle at 78% 62%, rgba(255, 255, 255, 0.09) 0.35px, transparent 1.4px),
      radial-gradient(circle at 8% 38%, rgba(180, 220, 255, 0.1) 0.3px, transparent 1.2px),
      radial-gradient(circle at 36% 44%, rgba(255, 255, 255, 0.07) 0.25px, transparent 1px),
      radial-gradient(circle at 58% 36%, rgba(200, 230, 255, 0.08) 0.3px, transparent 1.2px),
      radial-gradient(circle at 92% 48%, rgba(255, 255, 255, 0.07) 0.25px, transparent 1px),
      radial-gradient(circle at 14% 82%, rgba(180, 220, 255, 0.08) 0.3px, transparent 1.2px);
    background-size:
      100% 100%,
      100% 100%,
      100% 100%,
      100% 100%,
      200px 190px,
      200px 190px,
      200px 190px,
      200px 190px,
      200px 190px,
      200px 190px,
      200px 190px,
      200px 190px,
      240px 220px,
      240px 220px,
      240px 220px,
      240px 220px;
    background-repeat:
      no-repeat,
      no-repeat,
      no-repeat,
      no-repeat,
      repeat,
      repeat,
      repeat,
      repeat,
      repeat,
      repeat,
      repeat,
      repeat,
      repeat,
      repeat,
      repeat,
      repeat;
  }

  .pmx-orb,
  .pmx-spark {
    position: absolute;
    pointer-events: none;
  }

  .pmx-orb {
    border-radius: 999px;
    filter: blur(18px);
    opacity: 0.88;
    animation: pmx-float 12s ease-in-out infinite;
  }

  .pmx-orb--a {
    top: -70px;
    left: -80px;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(102, 126, 234, 0.45), rgba(102, 126, 234, 0));
  }

  .pmx-orb--b {
    right: -90px;
    top: 14%;
    width: 240px;
    height: 240px;
    background: radial-gradient(circle, rgba(118, 75, 162, 0.4), rgba(118, 75, 162, 0));
    animation-duration: 14s;
    animation-delay: -4s;
  }

  .pmx-orb--c {
    bottom: -110px;
    right: 10%;
    width: 230px;
    height: 230px;
    background: radial-gradient(circle, rgba(255, 215, 0, 0.35), rgba(255, 215, 0, 0));
    animation-duration: 16s;
    animation-delay: -8s;
  }

  .pmx-spark {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(102, 126, 234, 0.45) 0%,
      rgba(118, 75, 162, 0.12) 38%,
      transparent 72%
    );
    box-shadow:
      0 0 28px rgba(102, 126, 234, 0.22),
      0 0 56px rgba(118, 75, 162, 0.08);
    filter: blur(5px);
    opacity: 0.3;
    animation: pmx-rise 9s ease-in-out infinite;
  }

  .pmx-content {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 24px 20px 10px;
  }

  .pmx-close {
    position: absolute;
    top: 14px;
    right: 14px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.06);
    color: #fff;
    cursor: pointer;
    transition:
      transform 180ms ease,
      background 180ms ease,
      border-color 180ms ease;
  }

  .pmx-close:hover {
    transform: rotate(90deg);
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.24);
  }

  .pmx-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .pmx-copy {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-top: 16px;
  }

  .pmx-brand {
    display: flex;
    justify-content: center;
    margin-bottom: 2px;
  }

  .pmx-logo {
    width: min(220px, 62%);
    height: auto;
    filter: drop-shadow(0 0 10px rgba(102, 126, 234, 0.3));
    animation: pmx-logo-glow 2.6s ease-in-out infinite alternate;
  }

  .pmx-benefits {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 18px;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .pmx-benefit {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    padding: 1px 0;
  }

  .pmx-benefit-mark {
    color: #4CAF50;
    font-weight: 800;
    flex: 0 0 auto;
    line-height: 1.4;
  }

  .pmx-benefit-text {
    color: #bbb;
    font-size: 12px;
    line-height: 1.4;
  }

  .pmx-benefit-text strong {
    color: #ffffff;
  }

  .pmx-stage {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 278px;
    padding: 4px 0 34px;
  }

  .pmx-ring {
    position: absolute;
    width: min(100%, 300px);
    aspect-ratio: 1;
    border-radius: 50%;
    background:
      radial-gradient(circle, transparent 52%, rgba(102, 126, 234, 0.05) 58%, transparent 68%),
      radial-gradient(circle, transparent 66%, rgba(255, 215, 0, 0.04) 72%, transparent 82%);
    filter: blur(3px);
    opacity: 0.55;
    animation: pmx-spin 18s linear infinite;
    pointer-events: none;
  }

  .pmx-carousel {
    position: relative;
    width: min(100%, 300px);
    height: 252px;
    perspective: 1200px;
  }

  .pmx-card {
    position: absolute;
    inset: 8px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 16px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 24px;
    background: linear-gradient(165deg, #252525, #1e1e1e);
    color: #fff;
    text-align: left;
    cursor: pointer;
    transition:
      transform 520ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity 260ms ease,
      box-shadow 260ms ease,
      filter 260ms ease;
    box-shadow: 0 22px 55px rgba(2, 6, 18, 0.42);
    overflow: hidden;
  }

  .pmx-card::before {
    content: "";
    position: absolute;
    inset: 0;
    background:
      linear-gradient(145deg, rgba(255, 255, 255, 0.08), transparent 22%),
      radial-gradient(circle at top right, rgba(255, 255, 255, 0.08), transparent 24%);
    pointer-events: none;
  }

  .pmx-card::after {
    content: "";
    position: absolute;
    inset: 1px;
    border-radius: 23px;
    border: 1px solid rgba(255, 255, 255, 0.05);
    pointer-events: none;
  }

  .pmx-card:not([data-active="true"]) {
    filter: blur(5px) saturate(0.9);
  }

  .pmx-card[data-active="true"] {
    background: linear-gradient(165deg, #252525, #1e1e1e);
    box-shadow:
      0 28px 70px rgba(3, 7, 20, 0.52),
      0 0 0 1px rgba(255, 255, 255, 0.05);
    filter: saturate(1.08) blur(0);
  }

  .pmx-card[data-active="true"]::before {
    display: none;
  }

  .pmx-card[data-featured="true"][data-active="true"] {
    box-shadow:
      0 34px 78px rgba(3, 7, 20, 0.56),
      0 0 28px rgba(255, 215, 0, 0.18);
  }

  .pmx-card[data-highlighted="true"] .pmx-card-glow {
    opacity: 0.46;
    filter: blur(22px);
  }

  .pmx-card[data-highlighted="true"] .pmx-card-tagline {
    text-shadow: 0 0 14px rgba(102, 126, 234, 0.45);
  }

  .pmx-card[data-highlighted="true"][data-active="true"] {
    box-shadow:
      0 36px 82px rgba(3, 7, 20, 0.58),
      0 0 40px rgba(102, 126, 234, 0.38),
      0 0 0 1px rgba(118, 75, 162, 0.42);
    filter: saturate(1.22) blur(0);
  }

  .pmx-card[data-highlighted="true"]:not([data-active="true"]) {
    filter: blur(5px) saturate(1.06);
  }

  .pmx-card-glow {
    position: absolute;
    inset: auto -18% -30% -18%;
    height: 130px;
    opacity: 0.24;
    filter: blur(18px);
    pointer-events: none;
  }

  .pmx-card-ribbon {
    position: absolute;
    top: 14px;
    right: 14px;
    padding: 6px 10px;
    border-radius: 999px;
    background: linear-gradient(45deg, #ffd700, #ffed4e);
    color: #000;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .pmx-card-ribbon--highlight {
    background: linear-gradient(45deg, #667eea, #764ba2);
    color: #fff;
    box-shadow: 0 0 18px rgba(102, 126, 234, 0.45);
  }

  .pmx-card-top {
    position: relative;
    z-index: 1;
  }

  .pmx-card-tagline {
    display: inline-block;
    margin-bottom: 8px;
    color: var(--accent-start);
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .pmx-card-title {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.03em;
  }

  .pmx-card-copy {
    margin: 8px 0 0;
    color: #bbb;
    font-size: 11px;
    line-height: 1.35;
  }

  .pmx-price {
    position: relative;
    z-index: 1;
    margin-top: 18px;
    font-size: 30px;
    font-weight: 800;
    letter-spacing: -0.05em;
    line-height: 1;
    color: #4CAF50;
  }

  .pmx-price span {
    margin-left: 4px;
    color: #bbb;
    font-size: 13px;
    font-weight: 600;
  }

  .pmx-card-bottom {
    position: relative;
    z-index: 1;
    display: grid;
    gap: 6px;
    margin-top: 12px;
  }

  .pmx-meta {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 13px;
    background: rgba(255, 255, 255, 0.05);
    font-size: 11px;
    color: #bbb;
  }

  .pmx-meta strong {
    color: #ffffff;
    font-weight: 700;
  }

  .pmx-nav {
    position: absolute;
    left: 50%;
    bottom: -2px;
    display: flex;
    align-items: center;
    gap: 10px;
    transform: translateX(-50%);
  }

  .pmx-arrow,
  .pmx-dot {
    border: 0;
    cursor: pointer;
  }

  .pmx-arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    transition: transform 180ms ease, background 180ms ease;
  }

  .pmx-arrow:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.14);
  }

  .pmx-dots {
    display: flex;
    gap: 8px;
  }

  .pmx-dot {
    width: 8px;
    height: 8px;
    padding: 0;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.22);
    transition: transform 180ms ease, background 180ms ease;
  }

  .pmx-dot[data-active="true"] {
    transform: scale(1.35);
    background: linear-gradient(45deg, #667eea, #764ba2);
  }

  .pmx-selection {
    padding: 12px 14px;
    border-radius: 18px;
    border: 1px solid rgba(255, 255, 255, 0.09);
    background: rgba(30, 30, 30, 0.7);
    box-shadow: 0 14px 28px rgba(1, 6, 18, 0.2);
  }

  .pmx-selection-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
  }

  .pmx-selection-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #888;
  }

  .pmx-selection-badge {
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(255, 215, 0, 0.12);
    color: #ffd700;
    font-size: 10px;
    font-weight: 700;
  }

  .pmx-selection-badge--highlight {
    background: rgba(102, 126, 234, 0.18);
    color: #8fa4ff;
    box-shadow: 0 0 14px rgba(102, 126, 234, 0.22);
  }

  .pmx-selection-main {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
  }

  .pmx-selection-plan {
    margin: 0;
    font-size: 19px;
    font-weight: 800;
    letter-spacing: -0.03em;
  }

  .pmx-selection-price {
    margin: 0;
    color: #4CAF50;
    font-size: 14px;
    white-space: nowrap;
    font-weight: 700;
  }

  .pmx-selection-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    margin-top: 10px;
  }

  .pmx-selection-stat {
    padding: 8px 10px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
  }

  .pmx-selection-stat span {
    display: block;
    margin-bottom: 4px;
    font-size: 10px;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .pmx-selection-stat strong {
    font-size: 12px;
    font-weight: 700;
    color: #fff;
  }

  .pmx-actions {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .pmx-status {
    text-align: center;
    font-size: 12px;
    color: #bbb;
    min-height: 16px;
  }

  .pmx-status[data-tone="success"] {
    color: #4CAF50;
  }

  .pmx-status[data-tone="error"] {
    color: #ff6b6b;
  }

  @property --pmx-beam-angle {
    syntax: "<angle>";
    inherits: false;
    initial-value: 0deg;
  }

  .pmx-beam-wrap {
    position: relative;
    width: 100%;
    border-radius: 999px;
    padding: 1px;
    background:
      conic-gradient(
        from var(--pmx-beam-angle),
        transparent 24deg,
        rgba(102, 126, 234, 0) 24deg,
        rgba(102, 126, 234, 0.95) 74deg,
        rgba(255, 215, 0, 0.95) 100deg,
        rgba(118, 75, 162, 0.95) 126deg,
        rgba(0, 176, 255, 0.95) 152deg,
        rgba(102, 126, 234, 0) 200deg,
        transparent 200deg
      ),
      #303030;
    animation: pmx-beam-spin 5s linear infinite;
    box-shadow: 0 10px 28px rgba(102, 126, 234, 0.22);
    transition: box-shadow 220ms ease, transform 160ms ease;
  }

  .pmx-beam-outer-glow {
    position: absolute;
    inset: -3px;
    border-radius: 999px;
    background: conic-gradient(
      from var(--pmx-beam-angle),
      transparent 24deg,
      rgba(102, 126, 234, 0) 24deg,
      rgba(102, 126, 234, 0.95) 74deg,
      rgba(255, 215, 0, 0.95) 100deg,
      rgba(118, 75, 162, 0.95) 126deg,
      rgba(0, 176, 255, 0.95) 152deg,
      rgba(102, 126, 234, 0) 200deg,
      transparent 200deg
    );
    filter: blur(14px);
    opacity: 0.38;
    animation: pmx-beam-spin 5s linear infinite;
    pointer-events: none;
    z-index: 0;
  }

  .pmx-primary {
    position: relative;
    display: block;
    width: 100%;
    isolation: isolate;
    overflow: hidden;
    padding: 15px 22px;
    border: 0;
    border-radius: 999px;
    background: #141414;
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: transform 160ms ease;
    backdrop-filter: saturate(120%) blur(4px);
  }

  .pmx-primary:disabled {
    cursor: not-allowed;
    opacity: 0.75;
  }

  .pmx-beam-inner-glow {
    position: absolute;
    inset: -20%;
    border-radius: 999px;
    background: conic-gradient(
      from var(--pmx-beam-angle),
      transparent 24deg,
      rgba(102, 126, 234, 0) 24deg,
      rgba(102, 126, 234, 0.95) 74deg,
      rgba(255, 215, 0, 0.95) 100deg,
      rgba(118, 75, 162, 0.95) 126deg,
      rgba(0, 176, 255, 0.95) 152deg,
      rgba(102, 126, 234, 0) 200deg,
      transparent 200deg
    );
    filter: blur(42px);
    opacity: 0.42;
    animation: pmx-beam-spin 5s linear infinite;
    pointer-events: none;
    z-index: 0;
  }

  .pmx-beam-particles {
    position: absolute;
    inset: 0;
    border-radius: 999px;
    overflow: hidden;
    pointer-events: none;
    z-index: 1;
    opacity: 0.92;
    -webkit-mask-image: radial-gradient(
      ellipse 95% 88% at 50% 50%,
      #000 35%,
      rgba(0, 0, 0, 0.75) 70%,
      transparent 100%
    );
    mask-image: radial-gradient(
      ellipse 95% 88% at 50% 50%,
      #000 35%,
      rgba(0, 0, 0, 0.75) 70%,
      transparent 100%
    );
  }

  .pmx-beam-particle {
    position: absolute;
    border-radius: 999px;
    background: radial-gradient(
      circle,
      rgba(255, 255, 255, 0.95) 0%,
      rgba(255, 215, 0, 0.85) 35%,
      rgba(102, 126, 234, 0.6) 70%,
      transparent 100%
    );
    box-shadow:
      0 0 6px rgba(255, 255, 255, 0.85),
      0 0 14px rgba(255, 215, 0, 0.55),
      0 0 22px rgba(102, 126, 234, 0.35);
    transform: translate(-50%, -50%);
    animation:
      pmx-beam-spin 5s linear infinite,
      pmx-beam-drift var(--pmx-drift-dur, 14s) ease-in-out infinite,
      pmx-beam-twinkle var(--pmx-twinkle-dur, 2s) ease-in-out infinite;
    will-change: transform, opacity;
  }

  .pmx-beam-particle--bright {
    background: radial-gradient(
      circle,
      #ffffff 0%,
      rgba(255, 237, 78, 0.95) 30%,
      rgba(102, 126, 234, 0.7) 65%,
      transparent 100%
    );
    box-shadow:
      0 0 8px rgba(255, 255, 255, 1),
      0 0 18px rgba(255, 215, 0, 0.85),
      0 0 28px rgba(118, 75, 162, 0.55),
      0 0 36px rgba(0, 176, 255, 0.25);
  }

  .pmx-beam-particle--soft {
    background: radial-gradient(
      circle,
      rgba(255, 215, 0, 0.5) 0%,
      rgba(102, 126, 234, 0.35) 45%,
      transparent 75%
    );
    box-shadow:
      0 0 20px rgba(255, 215, 0, 0.35),
      0 0 32px rgba(102, 126, 234, 0.25);
    filter: blur(var(--pmx-p-blur, 1.5px));
  }

  .pmx-beam-wrap:hover .pmx-beam-particles {
    opacity: 1;
  }

  .pmx-beam-wrap:hover .pmx-beam-particle {
    box-shadow:
      0 0 10px rgba(255, 255, 255, 0.95),
      0 0 20px rgba(255, 215, 0, 0.75),
      0 0 32px rgba(102, 126, 234, 0.5);
  }

  .pmx-beam-wrap:hover .pmx-beam-particle--bright {
    box-shadow:
      0 0 12px rgba(255, 255, 255, 1),
      0 0 24px rgba(255, 215, 0, 0.95),
      0 0 40px rgba(118, 75, 162, 0.65),
      0 0 48px rgba(0, 176, 255, 0.35);
  }

  .pmx-beam-glare {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      -45deg,
      transparent 20%,
      rgba(255, 255, 255, 0.1) 50%,
      transparent 80%
    );
    transform: translateX(-100%);
    pointer-events: none;
    z-index: 3;
    transition: transform 650ms ease-in-out;
  }

  .pmx-primary-label {
    position: relative;
    z-index: 4;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    -webkit-mask-image: linear-gradient(to bottom, #000 50%, rgba(0, 0, 0, 0.55) 100%);
    mask-image: linear-gradient(to bottom, #000 50%, rgba(0, 0, 0, 0.55) 100%);
  }

  .pmx-primary-icon {
    flex: 0 0 auto;
    transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .pmx-beam-wrap:hover {
    transform: translateY(-2px);
    box-shadow:
      0 14px 34px rgba(102, 126, 234, 0.34),
      0 0 24px rgba(118, 75, 162, 0.2);
  }

  .pmx-beam-wrap:hover .pmx-beam-outer-glow {
    opacity: 0.52;
  }

  .pmx-beam-wrap:hover .pmx-beam-glare {
    transform: translateX(100%);
  }

  .pmx-beam-wrap:hover .pmx-primary-icon {
    transform: translateX(4px);
  }

  .pmx-beam-wrap:active {
    transform: translateY(0);
  }

  @keyframes pmx-beam-spin {
    to {
      --pmx-beam-angle: 360deg;
    }
  }

  @keyframes pmx-beam-drift {
    0%,
    100% {
      translate: 0 0;
    }
    20% {
      translate: 6px -5px;
    }
    40% {
      translate: -5px 6px;
    }
    60% {
      translate: 5px 4px;
    }
    80% {
      translate: -4px -3px;
    }
  }

  @keyframes pmx-beam-twinkle {
    0%,
    100% {
      opacity: 0.35;
      scale: 0.75;
    }
    45% {
      opacity: 1;
      scale: 1.2;
    }
    70% {
      opacity: 0.65;
      scale: 0.95;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .pmx-beam-wrap,
    .pmx-beam-outer-glow,
    .pmx-beam-inner-glow,
    .pmx-beam-particles,
    .pmx-beam-particle {
      animation: none;
    }

    .pmx-beam-glare {
      display: none;
    }
  }

  @keyframes pmx-float {
    0%,
    100% {
      transform: translate3d(0, 0, 0) scale(1);
    }
    50% {
      transform: translate3d(0, 18px, 0) scale(1.08);
    }
  }

  @keyframes pmx-rise {
    0% {
      transform: translate3d(0, 0, 0) scale(0.5);
      opacity: 0;
    }
    18% {
      opacity: 0.38;
    }
    55% {
      opacity: 0.22;
    }
    100% {
      transform: translate3d(10px, -180px, 0) scale(1.6);
      opacity: 0;
    }
  }

  @keyframes pmx-spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes pmx-logo-glow {
    from {
      filter: drop-shadow(0 0 10px rgba(102, 126, 234, 0.3));
    }
    to {
      filter: drop-shadow(0 0 20px rgba(102, 126, 234, 0.6));
    }
  }

  @media (max-width: 640px) {
    .pmx-root {
      align-items: center;
      padding:
        max(10px, env(safe-area-inset-top))
        max(10px, env(safe-area-inset-right))
        max(10px, env(safe-area-inset-bottom))
        max(10px, env(safe-area-inset-left));
    }

    .pmx-window {
      width: 100%;
      max-width: none;
      border-radius: 20px;
      box-shadow:
        0 25px 50px rgba(0, 0, 0, 0.5),
        inset 0 1px 0 rgba(255, 255, 255, 0.1);
    }

    .pmx-content {
      padding: 14px 14px 16px;
      gap: 10px;
    }

    .pmx-close {
      top: 10px;
      right: 10px;
      width: 36px;
      height: 36px;
    }

    .pmx-copy {
      gap: 8px;
      padding-top: 6px;
    }

    .pmx-logo {
      width: min(168px, 52%);
    }

    .pmx-benefits {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 10px;
      gap: 4px;
    }

    .pmx-benefit {
      gap: 6px;
    }

    .pmx-benefit-text {
      font-size: 11px;
      line-height: 1.35;
    }

    .pmx-stage {
      min-height: 214px;
      padding: 0 0 30px;
    }

    .pmx-ring {
      width: min(100%, 260px);
      opacity: 0.45;
    }

    .pmx-carousel {
      width: min(100%, calc(100vw - 56px));
      height: 204px;
      touch-action: manipulation;
    }

    .pmx-card {
      inset: 2px 0 0;
      padding: 13px 14px;
      border-radius: 20px;
    }

    .pmx-card::after {
      border-radius: 19px;
    }

    .pmx-card-ribbon {
      top: 10px;
      right: 10px;
      padding: 5px 8px;
      font-size: 9px;
    }

    .pmx-card-tagline {
      margin-bottom: 6px;
      font-size: 10px;
    }

    .pmx-card-title {
      font-size: 18px;
    }

    .pmx-card-copy {
      margin-top: 5px;
      font-size: 10px;
      line-height: 1.3;
    }

    .pmx-price {
      margin-top: 10px;
      font-size: 26px;
    }

    .pmx-price span {
      font-size: 12px;
    }

    .pmx-card-bottom {
      margin-top: 8px;
      gap: 4px;
    }

    .pmx-meta {
      padding: 6px 8px;
      border-radius: 11px;
      font-size: 10px;
    }

    .pmx-nav {
      bottom: 0;
      gap: 10px;
    }

    .pmx-arrow {
      width: 40px;
      height: 40px;
    }

    .pmx-dot {
      width: 7px;
      height: 7px;
    }

    .pmx-dots {
      gap: 7px;
    }

    .pmx-selection {
      padding: 10px 12px;
      border-radius: 16px;
    }

    .pmx-selection-top {
      margin-bottom: 6px;
    }

    .pmx-selection-plan {
      font-size: 17px;
    }

    .pmx-selection-price {
      font-size: 13px;
    }

    .pmx-selection-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 6px;
      margin-top: 8px;
    }

    .pmx-selection-stat {
      padding: 7px 9px;
      border-radius: 11px;
    }

    .pmx-selection-stat strong {
      font-size: 11px;
    }

    .pmx-selection-main {
      flex-direction: row;
      align-items: baseline;
      gap: 12px;
    }

    .pmx-primary {
      padding: 15px 18px;
      font-size: 15px;
    }

    .pmx-orb--a {
      top: -48px;
      left: -56px;
      width: 150px;
      height: 150px;
    }

    .pmx-orb--b {
      right: -64px;
      width: 170px;
      height: 170px;
    }

    .pmx-orb--c {
      bottom: -72px;
      width: 160px;
      height: 160px;
    }

    .pmx-spark {
      width: 16px;
      height: 16px;
      filter: blur(4px);
    }
  }

  @media (max-width: 640px) and (max-height: 740px) {
    .pmx-stage {
      min-height: 200px;
      padding-bottom: 28px;
    }

    .pmx-carousel {
      height: 190px;
    }
  }

  @media (max-width: 640px) and (max-height: 660px) {
    .pmx-benefits {
      display: none;
    }

    .pmx-copy {
      padding-top: 2px;
    }

    .pmx-logo {
      width: min(148px, 48%);
    }
  }

  @media (max-width: 380px) {
    .pmx-content {
      padding: 12px 12px 14px;
      gap: 8px;
    }

    .pmx-benefits {
      grid-template-columns: 1fr;
    }

    .pmx-carousel {
      width: min(100%, calc(100vw - 48px));
      height: 188px;
    }

    .pmx-card-title {
      font-size: 17px;
    }

    .pmx-price {
      font-size: 24px;
    }

    .pmx-selection-main {
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
    }
  }

  /*
   * Mobile performance: на телефонах отключаем самые тяжёлые непрерывные
   * эффекты (blur-фильтры, backdrop-filter, частицы Glowing Beam, анимации
   * фоновых слоёв), чтобы перелистывание карточек было плавным.
   * Десктоп (>640px) сохраняет полный визуал.
   */
  @media (max-width: 640px) {
    .pmx-backdrop {
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      background: rgba(0, 0, 0, 0.9);
    }

    .pmx-noise,
    .pmx-orb,
    .pmx-spark,
    .pmx-ring,
    .pmx-beam-particles,
    .pmx-beam-glare {
      display: none;
    }

    .pmx-beam-wrap,
    .pmx-beam-outer-glow,
    .pmx-beam-inner-glow {
      animation: none;
    }

    .pmx-card {
      transition:
        transform 360ms cubic-bezier(0.22, 1, 0.36, 1),
        opacity 220ms ease;
      box-shadow: 0 12px 30px rgba(2, 6, 18, 0.45);
      will-change: transform, opacity;
    }

    /* Неактивные карточки — без blur, только прозрачность/масштаб */
    .pmx-card:not([data-active="true"]),
    .pmx-card[data-highlighted="true"]:not([data-active="true"]) {
      filter: none;
    }

    .pmx-card[data-active="true"],
    .pmx-card[data-featured="true"][data-active="true"],
    .pmx-card[data-highlighted="true"][data-active="true"] {
      filter: none;
      box-shadow: 0 14px 34px rgba(2, 6, 18, 0.5);
    }
  }
`;

function formatStars(value) {
  return new Intl.NumberFormat("ru-RU").format(value);
}

function getVisualOffset(index, activeIndex, total) {
  let offset = index - activeIndex;

  if (offset > total / 2) {
    offset -= total;
  }

  if (offset < -total / 2) {
    offset += total;
  }

  return offset;
}

function useCompactCarousel() {
  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 640px)").matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 640px)");
    const handleChange = (event) => setCompact(event.matches);

    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return compact;
}

function Spark({ top, left, delay, duration }) {
  return (
    <span
      className="pmx-spark"
      style={{
        top,
        left,
        animationDelay: delay,
        animationDuration: duration,
      }}
    />
  );
}

const PremiumModal = ({
  isOpen,
  onClose = () => {},
  onSelectPlan,
  plans: plansProp,
  isProcessing = false,
  statusMessage = null,
  statusTone = null,
}) => {
  const plans = useMemo(
    () =>
      Array.isArray(plansProp) && plansProp.length > 0
        ? plansProp
        : DEFAULT_PLANS,
    [plansProp],
  );

  const featuredIndex = useMemo(() => {
    const idx = plans.findIndex((plan) => plan.featured);
    return idx >= 0 ? idx : 0;
  }, [plans]);

  const [activeIndex, setActiveIndex] = useState(featuredIndex);
  const touchStartRef = useRef(0);
  const compactCarousel = useCompactCarousel();

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    setActiveIndex(featuredIndex);
  }, [isOpen, featuredIndex]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }

      if (event.key === "ArrowRight") {
        setActiveIndex((currentIndex) => (currentIndex + 1) % plans.length);
      }

      if (event.key === "ArrowLeft") {
        setActiveIndex(
          (currentIndex) => (currentIndex - 1 + plans.length) % plans.length,
        );
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, plans.length]);

  if (!isOpen) {
    return null;
  }

  const activePlan = plans[activeIndex] || plans[0];

  const goToPreviousPlan = () => {
    setActiveIndex(
      (currentIndex) => (currentIndex - 1 + plans.length) % plans.length,
    );
  };

  const goToNextPlan = () => {
    setActiveIndex((currentIndex) => (currentIndex + 1) % plans.length);
  };

  const handleTouchStart = (event) => {
    touchStartRef.current = event.changedTouches[0].clientX;
  };

  const handleTouchEnd = (event) => {
    const deltaX = event.changedTouches[0].clientX - touchStartRef.current;

    if (Math.abs(deltaX) < 40) {
      return;
    }

    if (deltaX > 0) {
      goToPreviousPlan();
      return;
    }

    goToNextPlan();
  };

  const handleContinue = () => {
    if (isProcessing) return;
    if (typeof onSelectPlan === "function") {
      onSelectPlan(activePlan);
    }
  };

  return (
    <div className="pmx-root">
      <style>{styles}</style>
      <div className="pmx-backdrop" onClick={onClose} />

      <div
        className="pmx-window"
        role="dialog"
        aria-modal="true"
        aria-labelledby="premium-modal-title"
      >
        <div className="pmx-noise" />
        <div className="pmx-orb pmx-orb--a" />
        <div className="pmx-orb pmx-orb--b" />
        <div className="pmx-orb pmx-orb--c" />

        <Spark top="18%" left="12%" delay="0s" duration="9s" />
        <Spark top="70%" left="8%" delay="2.2s" duration="10s" />
        <Spark top="54%" left="84%" delay="4.5s" duration="9.5s" />
        <Spark top="24%" left="76%" delay="6.8s" duration="10.5s" />

        <button
          type="button"
          className="pmx-close"
          onClick={onClose}
          aria-label="Close premium modal"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M6 6L18 18M18 6L6 18"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="pmx-content">
          <div className="pmx-copy">
            <h2 id="premium-modal-title" className="pmx-sr-only">
              CGPeers premium subscriptions
            </h2>

            <div className="pmx-brand">
              <img
                className="pmx-logo"
                src="/cgp_logo_app.png"
                alt="CGPeers"
              />
            </div>

            <ul className="pmx-benefits">
              {BENEFITS.map((benefit) => (
                <li key={benefit.id} className="pmx-benefit">
                  <span className="pmx-benefit-mark">✓</span>
                  <span className="pmx-benefit-text">
                    {benefit.prefix ?? ""}
                    <strong>{benefit.strong}</strong>
                    {benefit.suffix ?? ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="pmx-stage">
            <div className="pmx-ring" />
            <div
              className="pmx-carousel"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {plans.map((plan, index) => {
                const offset = getVisualOffset(index, activeIndex, plans.length);
                const isVisible = Math.abs(offset) <= 1;
                const isActive = offset === 0;
                const scale = isActive ? 1 : compactCarousel ? 0.9 : 0.87;
                const rotate = isActive ? 0 : offset * (compactCarousel ? 5 : 8);
                const translateX = offset * (compactCarousel ? 24 : 36);
                const translateY = isActive ? 0 : compactCarousel ? 18 : 26;

                return (
                  <button
                    key={plan.id}
                    type="button"
                    className="pmx-card"
                    data-active={isActive}
                    data-featured={Boolean(plan.featured)}
                    data-highlighted={Boolean(plan.highlighted)}
                    aria-label={`${plan.label} for ${formatStars(plan.price)} Stars`}
                    onClick={() => setActiveIndex(index)}
                    style={{
                      "--accent-start": plan.accentStart,
                      opacity: isVisible ? (isActive ? 1 : 0.74) : 0,
                      pointerEvents: isVisible ? "auto" : "none",
                      zIndex: isActive ? 3 : 2 - Math.abs(offset),
                      transform: `translateX(${translateX}%) translateY(${translateY}px) scale(${scale}) rotate(${rotate}deg)`,
                    }}
                  >
                    <span
                      className="pmx-card-glow"
                      style={{
                        background: plan.accentEnd
                          ? `radial-gradient(circle, ${plan.accentStart} 0%, ${plan.accentEnd} 42%, transparent 72%)`
                          : `radial-gradient(circle, ${plan.accentStart} 0%, transparent 70%)`,
                      }}
                    />

                    {plan.highlighted ? (
                      <span className="pmx-card-ribbon pmx-card-ribbon--highlight">
                        {plan.highlightLabel ?? "Best value"}
                      </span>
                    ) : plan.featured ? (
                      <span className="pmx-card-ribbon">Recommended</span>
                    ) : null}

                    <div className="pmx-card-top">
                      <span className="pmx-card-tagline">{plan.tagline}</span>
                      <h3 className="pmx-card-title">{plan.label}</h3>
                      <p className="pmx-card-copy">{plan.blurb}</p>
                    </div>

                    <div>
                      <div className="pmx-price">
                        {formatStars(plan.price)} <span>Stars</span>
                      </div>

                      <div className="pmx-card-bottom">
                        <div className="pmx-meta">
                          <span>Access</span>
                          <strong>{plan.duration}</strong>
                        </div>
                        <div className="pmx-meta">
                          <span>Average</span>
                          <strong>{plan.monthly}</strong>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="pmx-nav">
              <button
                type="button"
                className="pmx-arrow"
                onClick={goToPreviousPlan}
                aria-label="Show previous plan"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M14.5 5L8 12L14.5 19"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              <div className="pmx-dots">
                {plans.map((plan, index) => (
                  <button
                    key={plan.id}
                    type="button"
                    className="pmx-dot"
                    data-active={index === activeIndex}
                    onClick={() => setActiveIndex(index)}
                    aria-label={`Show ${plan.label}`}
                  />
                ))}
              </div>

              <button
                type="button"
                className="pmx-arrow"
                onClick={goToNextPlan}
                aria-label="Show next plan"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9.5 5L16 12L9.5 19"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>

          <div className="pmx-selection">
            <div className="pmx-selection-top">
              <span className="pmx-selection-label">Selected plan</span>
              {activePlan.highlighted ? (
                <span className="pmx-selection-badge pmx-selection-badge--highlight">
                  {activePlan.highlightLabel ?? "Best value"}
                </span>
              ) : activePlan.featured ? (
                <span className="pmx-selection-badge">Recommended</span>
              ) : null}
            </div>

            <div className="pmx-selection-main">
              <h3 className="pmx-selection-plan">{activePlan.label}</h3>
              <p className="pmx-selection-price">
                {formatStars(activePlan.price)} Stars
              </p>
            </div>

            <div className="pmx-selection-grid">
              <div className="pmx-selection-stat">
                <span>Access window</span>
                <strong>{activePlan.duration}</strong>
              </div>
              <div className="pmx-selection-stat">
                <span>Monthly pace</span>
                <strong>{activePlan.monthly}</strong>
              </div>
            </div>
          </div>

          <div className="pmx-actions">
            {statusMessage ? (
              <div className="pmx-status" data-tone={statusTone || undefined}>
                {statusMessage}
              </div>
            ) : null}
            <div className="pmx-beam-wrap">
              <span className="pmx-beam-outer-glow" aria-hidden="true" />
              <button
                type="button"
                className="pmx-primary"
                onClick={handleContinue}
                disabled={isProcessing}
              >
                <span className="pmx-beam-inner-glow" aria-hidden="true" />
                <span className="pmx-beam-particles" aria-hidden="true">
                  {BEAM_PARTICLES.map((particle, index) => (
                    <span
                      key={index}
                      className={[
                        "pmx-beam-particle",
                        particle.bright && "pmx-beam-particle--bright",
                        particle.soft && "pmx-beam-particle--soft",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      style={{
                        top: `${particle.top}%`,
                        left: `${particle.left}%`,
                        width: `${particle.size}px`,
                        height: `${particle.size}px`,
                        "--pmx-drift-dur": `${particle.drift}s`,
                        "--pmx-twinkle-dur": `${particle.twinkle}s`,
                        "--pmx-p-blur": `${particle.blur + (particle.soft ? 1.2 : 0)}px`,
                        animationDelay: `0s, ${particle.delay}s, ${particle.delay * 0.6}s`,
                        filter: particle.soft
                          ? undefined
                          : `blur(${particle.blur}px)`,
                      }}
                    />
                  ))}
                </span>
                <span className="pmx-beam-glare" aria-hidden="true" />
                <span className="pmx-primary-label">
                  {isProcessing
                    ? "Processing..."
                    : `Continue with ${formatStars(activePlan.price)} Stars`}
                  <svg
                    className="pmx-primary-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M9.5 5L16 12L9.5 19"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumModal;
